import { ScoreLogForm } from '@/components/custom/ScoreLogForm';
import { ScoreModalShell } from '@/components/custom/ScoreModalLayout';
import { CalendarDateFilter } from '@/components/programming/CalendarDateFilter';
import { CalendarView } from '@/components/programming/CalendarView';
import { ScoreActionButton } from '@/components/programming/ScoreActionButton';
import { type ProgrammingViewMode, ViewToggle } from '@/components/programming/ViewToggle';
import { WorkoutCreatorMetadata } from '@/components/programming/WorkoutCreatorMetadata';
import { WorkoutDetailDialog } from '@/components/programming/WorkoutDetailDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDateWithWeekday } from '@/lib/utils';
import { useMinimizedWorkoutStore } from '@/stores/minimizedWorkoutStore';
import { type WorkoutAttribution, getProgrammingScoringLabel } from '@ergshoot/shared';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Dumbbell,
  Loader2,
  Monitor,
  Pencil,
  Plus,
  Star,
  Target,
  Trophy,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { filterByCalendarDate, getCalendarDate } from './calendarNavigation';

export type ProgrammingTrackContext = 'daily' | 'standards' | 'specialty' | 'mine';

export interface StaffProgrammingWorkout {
  id: string;
  name: string;
  description?: string | null;
  scheduled_date?: string | null;
  master_scheduled_date?: string | null;
  track?: { id?: string | null; name?: string | null } | null;
  track_id?: string | null;
  track_name?: string | null;
  movements?: string[] | string | null;
  equipment?: string[] | string | null;
  scoring_type?: string | null;
  scoring_direction?: string | null;
  duration_minutes?: number | null;
  category?: string | null;
  workout_type?: string | null;
  coach_notes?: string | null;
  instructions?: string | null;
  created_by_name?: string | null;
  created_by_username?: string | null;
  created_at?: string | null;
  attribution?: WorkoutAttribution | null;
  is_personal?: boolean;
  is_favorite?: boolean | number;
  is_creator?: boolean;
  time_slot?: string | null;
  current_score?: {
    value: number;
    display?: string | null;
    notes?: string | null;
    logged_at?: string | null;
  } | null;
}

export interface StaffProgrammingData {
  workouts: StaffProgrammingWorkout[];
  tracks?: Array<{ id: string; name: string }>;
}

interface StaffProgrammingExperienceProps {
  title?: string;
  description?: string;
  queryKey: readonly unknown[];
  fetchMonth: (params: { year: number; month: number }) => Promise<StaffProgrammingData>;
  primaryAction?: {
    label: string;
    icon?: 'calendar' | 'plus';
    onClick?: () => void;
    href?: string;
  };
  notice?: string;
  canEditWorkouts?: boolean;
  canEditWorkout?: (workout: NormalizedStaffWorkout) => boolean;
  canOpenTv?: boolean;
  canLogScores?: boolean;
  onEditWorkout?: (workout: NormalizedStaffWorkout) => void;
  onScoreLogged?: () => void;
  onScheduleWorkout?: (
    workout: NormalizedStaffWorkout,
    schedule: { scheduled_date: string; time_slot?: string }
  ) => Promise<void>;
  onToggleFavorite?: (workout: NormalizedStaffWorkout) => Promise<void>;
  showListMonthControls?: boolean;
  /** Specialty programmes are HQ-managed and hidden from non-HQ portals. */
  showSpecialty?: boolean;
  /** Show an All Workouts/Favourites filter within My Workouts. */
  showFavoriteFilter?: boolean;
}

export interface NormalizedStaffWorkout {
  id: string;
  name: string;
  description?: string | null;
  scheduled_date?: string;
  master_scheduled_date?: string;
  track: { id: string | null; name: string | null };
  movements: string[];
  equipment: string[];
  scoring_type?: string;
  scoring_direction?: string;
  duration_minutes?: number | null;
  category?: string | null;
  workout_type?: string | null;
  coach_notes?: string | null;
  instructions?: string | null;
  created_by_name?: string | null;
  created_by_username?: string | null;
  created_at?: string | null;
  attribution?: WorkoutAttribution | null;
  is_personal: boolean;
  is_favorite: boolean;
  is_creator: boolean;
  time_slot?: string | null;
  current_score?: {
    value: number;
    display?: string | null;
    notes?: string | null;
    logged_at?: string | null;
  } | null;
}

const trackTabs: Array<{
  id: ProgrammingTrackContext;
  label: string;
  description: string;
}> = [
  {
    id: 'daily',
    label: 'Daily WODs',
    description: 'Scheduled daily programming',
  },
  {
    id: 'standards',
    label: 'Standards',
    description: 'Benchmark workouts',
  },
  {
    id: 'specialty',
    label: 'Specialty',
    description: 'Track-specific sessions',
  },
  {
    id: 'mine',
    label: 'My Workouts',
    description: 'Workouts created by you or saved as Favourites',
  },
];

export function getVisibleProgrammingTrackTabs(showSpecialty = true) {
  return showSpecialty ? trackTabs : trackTabs.filter((tab) => tab.id !== 'specialty');
}

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function parseProgrammingListField(raw: string[] | string | null | undefined): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((item) => String(item)).filter(Boolean);

  const trimmed = raw.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item)).filter(Boolean);
      }
    } catch {
      // Fall through to text splitting.
    }
  }

  return trimmed
    .split('|')
    .flatMap((part) => part.split('\n'))
    .map((item) => item.trim())
    .filter(Boolean);
}

export function normalizeStaffWorkout(workout: StaffProgrammingWorkout): NormalizedStaffWorkout {
  const effectiveScheduledDate = workout.scheduled_date || workout.master_scheduled_date;

  return {
    id: workout.id,
    name: workout.name,
    description: workout.description,
    // HQ programming keeps its master date separate from a user's optional
    // personal placement. Use the master date until the user chooses another.
    scheduled_date: effectiveScheduledDate?.slice(0, 10),
    master_scheduled_date: workout.master_scheduled_date?.slice(0, 10),
    track: {
      id: workout.track?.id ?? workout.track_id ?? null,
      name: workout.track?.name ?? workout.track_name ?? null,
    },
    movements: parseProgrammingListField(workout.movements),
    equipment: parseProgrammingListField(workout.equipment),
    scoring_type: workout.scoring_type ?? undefined,
    scoring_direction: workout.scoring_direction ?? undefined,
    duration_minutes: workout.duration_minutes,
    category: workout.category,
    workout_type: workout.workout_type,
    coach_notes: workout.coach_notes,
    instructions: workout.instructions,
    created_by_name: workout.created_by_name,
    created_by_username: workout.created_by_username,
    created_at: workout.created_at,
    attribution: workout.attribution,
    is_personal: workout.is_personal ?? false,
    is_favorite: workout.is_favorite === true || Number(workout.is_favorite) === 1,
    is_creator: workout.is_creator ?? false,
    time_slot: workout.time_slot,
    current_score: workout.current_score,
  };
}

function formatCurrentScore(workout: NormalizedStaffWorkout): string | null {
  const score = workout.current_score;
  if (!score) return null;
  if (score.display) return score.display;
  if (workout.scoring_type === 'time') {
    const minutes = Math.floor(score.value / 60);
    const seconds = score.value % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  return String(score.value);
}

export function getProgrammingTrackContext(
  workout: NormalizedStaffWorkout
): ProgrammingTrackContext {
  if (workout.is_personal || workout.is_favorite) return 'mine';

  const trackId = workout.track.id?.toLowerCase() ?? '';
  const trackName = workout.track.name?.toLowerCase() ?? '';
  const workoutType = workout.workout_type?.toLowerCase() ?? '';

  if (
    workoutType === 'standard' ||
    trackId.includes('standards') ||
    trackName.includes('standard')
  ) {
    return 'standards';
  }

  if (trackId.includes('daily') || trackName.includes('daily wod') || trackName === 'daily wods') {
    return 'daily';
  }

  return workoutType === 'daily_wod' && !trackId && !trackName ? 'daily' : 'specialty';
}

export function isAffiliateOwnedWorkout(
  workout: Pick<NormalizedStaffWorkout, 'attribution'>
): boolean {
  return workout.attribution?.source === 'affiliate';
}

function moveMonth(date: Date, delta: number) {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

function PrimaryAction({
  action,
}: { action: NonNullable<StaffProgrammingExperienceProps['primaryAction']> }) {
  const Icon = action.icon === 'plus' ? Plus : Calendar;
  const content = (
    <>
      <Icon className="h-4 w-4 mr-2" />
      {action.label}
    </>
  );

  if (action.href) {
    return (
      <Button asChild>
        <a href={action.href}>{content}</a>
      </Button>
    );
  }

  return <Button onClick={action.onClick}>{content}</Button>;
}

function MonthControls({
  date,
  onChange,
}: {
  date: Date;
  onChange: (date: Date) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="icon" onClick={() => onChange(moveMonth(date, -1))}>
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <div className="min-w-[150px] text-center text-sm font-medium">
        {monthNames[date.getMonth()]} {date.getFullYear()}
      </div>
      <Button variant="outline" size="icon" onClick={() => onChange(moveMonth(date, 1))}>
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Button variant="outline" size="sm" onClick={() => onChange(new Date())}>
        Today
      </Button>
    </div>
  );
}

export function WorkoutCard({
  workout,
  canEdit,
  canOpenTv,
  canLogScores,
  onClick,
  onEdit,
  onScore,
  onSchedule,
  onFavorite,
  isFavoritePending = false,
}: {
  workout: NormalizedStaffWorkout;
  canEdit: boolean;
  canOpenTv: boolean;
  canLogScores: boolean;
  onClick: () => void;
  onEdit?: () => void;
  onScore: () => void;
  onSchedule?: () => void;
  onFavorite?: () => void | Promise<void>;
  isFavoritePending?: boolean;
}) {
  return (
    <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={onClick}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <CardTitle className="text-lg">{workout.name}</CardTitle>
            {workout.description && (
              <CardDescription className="line-clamp-2 whitespace-pre-wrap">
                {workout.description}
              </CardDescription>
            )}
          </div>
          {workout.track.name && <Badge variant="outline">{workout.track.name}</Badge>}
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          {workout.scoring_type && (
            <Badge variant="secondary" className="text-xs">
              {getProgrammingScoringLabel(workout.scoring_type)}
            </Badge>
          )}
          {workout.duration_minutes && (
            <Badge variant="secondary" className="text-xs">
              {workout.duration_minutes} min
            </Badge>
          )}
          {workout.scheduled_date && !workout.scheduled_date.startsWith('9999') && (
            <Badge variant="outline" className="text-xs">
              {formatDateWithWeekday(workout.scheduled_date)}
              {workout.time_slot ? ` at ${workout.time_slot}` : ''}
            </Badge>
          )}
          {workout.current_score && (
            <Badge variant="secondary" className="text-xs">
              Your score: {formatCurrentScore(workout)}
            </Badge>
          )}
          {workout.is_favorite && (
            <Badge variant="outline" className="text-xs">
              <Star className="mr-1 h-3 w-3 fill-current" />
              Favourite
            </Badge>
          )}
        </div>
        <WorkoutCreatorMetadata
          attribution={workout.attribution}
          created_at={workout.created_at}
          created_by_username={workout.created_by_username}
          className="pt-1"
        />
      </CardHeader>
      <CardContent className="space-y-3">
        {workout.movements.length > 0 && (
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">Workout:</span> {workout.movements.join(', ')}
          </p>
        )}
        {workout.equipment.length > 0 && (
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">Equipment:</span> {workout.equipment.join(', ')}
          </p>
        )}
        {(canOpenTv ||
          canEdit ||
          onSchedule ||
          onFavorite ||
          (canLogScores && workout.scoring_type)) && (
          <div className="flex flex-wrap gap-2 pt-2">
            {onSchedule && (
              <Button
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  onSchedule();
                }}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Add to Calendar
              </Button>
            )}
            {canLogScores && workout.scoring_type && (
              <ScoreActionButton hasScore={!!workout.current_score} onClick={onScore} />
            )}
            {onFavorite && (
              <Button
                variant={workout.is_favorite ? 'default' : 'outline'}
                size="sm"
                aria-label={workout.is_favorite ? 'Remove from Favourites' : 'Add to Favourites'}
                aria-pressed={workout.is_favorite}
                disabled={isFavoritePending}
                onClick={(event) => {
                  event.stopPropagation();
                  onFavorite();
                }}
              >
                <Star className={`h-4 w-4 mr-2 ${workout.is_favorite ? 'fill-current' : ''}`} />
                {workout.is_favorite ? 'Remove Favourite' : 'Favourite'}
              </Button>
            )}
            {canOpenTv && (
              <Button
                variant="outline"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(`/tv/${workout.id}`, '_blank');
                }}
              >
                <Monitor className="h-4 w-4 mr-2" />
                TV Display
              </Button>
            )}
            {canEdit && onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit();
                }}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function StaffProgrammingExperience({
  title = 'Programming',
  description = 'Find workouts by month, list, and programme section.',
  queryKey,
  fetchMonth,
  primaryAction,
  notice,
  canEditWorkouts = false,
  canEditWorkout,
  canOpenTv = true,
  canLogScores = false,
  onEditWorkout,
  onScoreLogged,
  onScheduleWorkout,
  onToggleFavorite,
  showListMonthControls = true,
  showSpecialty = true,
  showFavoriteFilter = false,
}: StaffProgrammingExperienceProps) {
  const scoreModalRestoreRef = useRef<HTMLHeadingElement | null>(null);
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [activeContext, setActiveContext] = useState<ProgrammingTrackContext>('daily');
  const [favouritesOnly, setFavouritesOnly] = useState(false);
  const [calendarDateFilter, setCalendarDateFilter] = useState<string | null>(null);
  const [selectedWorkout, setSelectedWorkout] = useState<NormalizedStaffWorkout | null>(null);
  const [scoringWorkout, setScoringWorkout] = useState<NormalizedStaffWorkout | null>(null);
  const [isScoreSubmissionPending, setIsScoreSubmissionPending] = useState(false);
  const [schedulingWorkout, setSchedulingWorkout] = useState<NormalizedStaffWorkout | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);
  const [favoriteMutationId, setFavoriteMutationId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ProgrammingViewMode>(() => {
    return (
      (localStorage.getItem('ergshoot-staff-programming-view') as ProgrammingViewMode) || 'calendar'
    );
  });
  const visibleTrackTabs = getVisibleProgrammingTrackTabs(showSpecialty);
  const effectiveActiveContext =
    !showSpecialty && activeContext === 'specialty' ? 'daily' : activeContext;

  useEffect(() => {
    if (!showSpecialty && activeContext === 'specialty') {
      setActiveContext('daily');
    }
  }, [activeContext, showSpecialty]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const { data, isLoading, error } = useQuery({
    queryKey: [...queryKey, year, month],
    queryFn: () => fetchMonth({ year, month: month + 1 }),
  });

  const workouts = useMemo(
    () => (data?.workouts || []).map(normalizeStaffWorkout),
    [data?.workouts]
  );

  const filteredWorkouts = useMemo(() => {
    const contextualWorkouts = workouts.filter(
      (workout) => getProgrammingTrackContext(workout) === effectiveActiveContext
    );
    if (!showFavoriteFilter || effectiveActiveContext !== 'mine' || !favouritesOnly) {
      return contextualWorkouts;
    }
    return contextualWorkouts.filter((workout) => workout.is_favorite);
  }, [effectiveActiveContext, favouritesOnly, showFavoriteFilter, workouts]);

  const visibleWorkouts = useMemo(
    () =>
      filterByCalendarDate(
        filteredWorkouts,
        calendarDateFilter,
        (workout) => workout.scheduled_date
      ),
    [calendarDateFilter, filteredWorkouts]
  );

  const counts = useMemo(() => {
    return workouts.reduce<Record<ProgrammingTrackContext, number>>(
      (acc, workout) => {
        acc[getProgrammingTrackContext(workout)] += 1;
        return acc;
      },
      { daily: 0, standards: 0, specialty: 0, mine: 0 }
    );
  }, [workouts]);

  const handleViewModeChange = (mode: ProgrammingViewMode) => {
    setViewMode(mode);
    if (mode === 'calendar') setCalendarDateFilter(null);
    localStorage.setItem('ergshoot-staff-programming-view', mode);
  };

  const handleCalendarDateClick = (date: string) => {
    setCalendarDateFilter(date);
    handleViewModeChange('list');
  };

  const handleListMonthChange = (date: Date) => {
    setCalendarDateFilter(null);
    setCurrentDate(date);
  };

  const canShowDetailEditAction =
    canEditWorkouts &&
    !!onEditWorkout &&
    !!selectedWorkout &&
    (canEditWorkout?.(selectedWorkout) ?? true);
  const canShowDetailScoreAction = canLogScores && !!selectedWorkout?.scoring_type;
  const openScoreModal = (workout: NormalizedStaffWorkout) => {
    setIsScoreSubmissionPending(false);
    setScoringWorkout(workout);
  };
  const handleFavoriteToggle = async (workout: NormalizedStaffWorkout) => {
    if (!onToggleFavorite || favoriteMutationId) return;
    setFavoriteMutationId(workout.id);
    try {
      await onToggleFavorite(workout);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update Favourite');
    } finally {
      setFavoriteMutationId(null);
    }
  };
  const canShowDetailFavoriteAction = !!selectedWorkout && !!onToggleFavorite;
  const detailActions =
    canShowDetailEditAction || canShowDetailScoreAction || canShowDetailFavoriteAction ? (
      <>
        {canShowDetailEditAction && (
          <Button
            variant="outline"
            onClick={() => {
              if (!selectedWorkout) return;
              onEditWorkout?.(selectedWorkout);
              setSelectedWorkout(null);
            }}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        )}
        {canShowDetailScoreAction && (
          <Button
            variant="outline"
            onClick={() => {
              if (!selectedWorkout) return;
              openScoreModal(selectedWorkout);
              setSelectedWorkout(null);
            }}
          >
            <Trophy className="mr-2 h-4 w-4" />
            Log Score
          </Button>
        )}
        {canShowDetailFavoriteAction && selectedWorkout && (
          <Button
            variant={selectedWorkout.is_favorite ? 'default' : 'outline'}
            disabled={favoriteMutationId !== null}
            onClick={async () => {
              await handleFavoriteToggle(selectedWorkout);
              setSelectedWorkout(null);
            }}
          >
            <Star className={`h-4 w-4 mr-2 ${selectedWorkout.is_favorite ? 'fill-current' : ''}`} />
            {selectedWorkout.is_favorite ? 'Remove Favourite' : 'Favourite'}
          </Button>
        )}
      </>
    ) : undefined;

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-destructive">Error loading programming: {(error as Error).message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1
            ref={scoreModalRestoreRef}
            tabIndex={-1}
            className="text-3xl font-bold text-gradient-gold"
          >
            {title}
          </h1>
          <p className="mt-1 text-muted-foreground">{description}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ViewToggle viewMode={viewMode} onChange={handleViewModeChange} />
          {primaryAction && <PrimaryAction action={primaryAction} />}
        </div>
      </div>

      {notice && (
        <Card className="border-dashed">
          <CardContent className="py-4 text-sm text-muted-foreground">{notice}</CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {visibleTrackTabs.map((tab) => {
          const isActive = effectiveActiveContext === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveContext(tab.id);
                setCalendarDateFilter(null);
                if (tab.id !== 'mine') setFavouritesOnly(false);
              }}
              className={`rounded-lg border p-4 text-left transition-colors ${
                isActive
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-semibold">{tab.label}</span>
                <Badge variant={isActive ? 'default' : 'outline'}>{counts[tab.id]}</Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{tab.description}</p>
            </button>
          );
        })}
      </div>

      {viewMode === 'list' && (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Dumbbell className="h-5 w-5 text-primary" />
              {visibleTrackTabs.find((tab) => tab.id === effectiveActiveContext)?.label}
            </h2>
            {showListMonthControls && (
              <MonthControls date={currentDate} onChange={handleListMonthChange} />
            )}
          </div>
          {showFavoriteFilter && effectiveActiveContext === 'mine' && (
            <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-card p-1">
              <Button
                type="button"
                size="sm"
                variant={favouritesOnly ? 'ghost' : 'default'}
                onClick={() => setFavouritesOnly(false)}
              >
                All Workouts
              </Button>
              <Button
                type="button"
                size="sm"
                variant={favouritesOnly ? 'default' : 'ghost'}
                onClick={() => setFavouritesOnly(true)}
              >
                Favourites
              </Button>
            </div>
          )}
          {calendarDateFilter && (
            <CalendarDateFilter
              date={calendarDateFilter}
              onClear={() => setCalendarDateFilter(null)}
            />
          )}
        </div>
      )}

      {viewMode === 'calendar' ? (
        <CalendarView
          key={`${year}-${month}`}
          initialYear={year}
          initialMonth={month}
          loading={isLoading}
          workouts={filteredWorkouts.map((workout) => ({
            id: workout.id,
            name: workout.name,
            category: workout.category || effectiveActiveContext,
            scoring_type: workout.scoring_type || 'reps',
            scheduled_date: workout.scheduled_date || '',
          }))}
          onDayClick={handleCalendarDateClick}
          onMonthChange={(nextYear, nextMonth) => setCurrentDate(new Date(nextYear, nextMonth, 1))}
          onWorkoutClick={(calendarWorkout) =>
            handleCalendarDateClick(getCalendarDate(calendarWorkout.scheduled_date))
          }
        />
      ) : visibleWorkouts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              No workouts found{showListMonthControls ? ' for this month' : ''}.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {showListMonthControls
                ? 'Try another month or programme section.'
                : 'Try another programme section.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {visibleWorkouts.map((workout) => (
            <WorkoutCard
              key={workout.id}
              workout={workout}
              canEdit={canEditWorkouts && (canEditWorkout?.(workout) ?? true)}
              canOpenTv={canOpenTv}
              canLogScores={canLogScores}
              onClick={() => setSelectedWorkout(workout)}
              onEdit={onEditWorkout ? () => onEditWorkout(workout) : undefined}
              onScore={() => openScoreModal(workout)}
              onFavorite={onToggleFavorite ? () => handleFavoriteToggle(workout) : undefined}
              isFavoritePending={favoriteMutationId !== null}
              onSchedule={
                onScheduleWorkout
                  ? () => {
                      setScheduleDate(workout.scheduled_date || '');
                      setScheduleTime(workout.time_slot || '');
                      setSchedulingWorkout(workout);
                    }
                  : undefined
              }
            />
          ))}
        </div>
      )}

      <WorkoutDetailDialog
        workout={selectedWorkout}
        open={!!selectedWorkout}
        onOpenChange={(open) => {
          if (!open) setSelectedWorkout(null);
        }}
        actions={detailActions}
        onMinimize={() => {
          if (selectedWorkout) {
            useMinimizedWorkoutStore.getState().minimize(selectedWorkout);
            setSelectedWorkout(null);
          }
        }}
      />

      {scoringWorkout?.scoring_type && (
        <ScoreModalShell
          ariaLabel={`Log score for ${scoringWorkout.name}`}
          canClose={!isScoreSubmissionPending}
          className="max-w-lg"
          onClose={() => setScoringWorkout(null)}
          restoreFocusRef={scoreModalRestoreRef}
        >
          <ScoreLogForm
            workoutId={scoringWorkout.id}
            workoutName={scoringWorkout.name}
            scoringType={scoringWorkout.scoring_type}
            onPendingChange={setIsScoreSubmissionPending}
            onSuccess={() => {
              setIsScoreSubmissionPending(false);
              setScoringWorkout(null);
              onScoreLogged?.();
            }}
            onCancel={() => {
              if (isScoreSubmissionPending) return;
              setScoringWorkout(null);
            }}
          />
        </ScoreModalShell>
      )}

      <Dialog
        open={!!schedulingWorkout}
        onOpenChange={(open) => {
          if (!open && !isScheduling) setSchedulingWorkout(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Calendar</DialogTitle>
            <DialogDescription>
              Choose when to add {schedulingWorkout?.name} to your personal calendar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="personal-schedule-date">Date</Label>
              <Input
                id="personal-schedule-date"
                type="date"
                value={scheduleDate}
                onInput={(event) => setScheduleDate(event.currentTarget.value)}
                onChange={(event) => setScheduleDate(event.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="personal-schedule-time">Time (optional)</Label>
              <Input
                id="personal-schedule-time"
                type="time"
                value={scheduleTime}
                onInput={(event) => setScheduleTime(event.currentTarget.value)}
                onChange={(event) => setScheduleTime(event.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={isScheduling}
              onClick={() => setSchedulingWorkout(null)}
            >
              Cancel
            </Button>
            <Button
              disabled={!scheduleDate || isScheduling}
              onClick={async () => {
                if (!schedulingWorkout || !scheduleDate || !onScheduleWorkout) return;
                setIsScheduling(true);
                try {
                  await onScheduleWorkout(schedulingWorkout, {
                    scheduled_date: scheduleDate,
                    time_slot: scheduleTime || undefined,
                  });
                  toast.success(`${schedulingWorkout.name} added to your calendar`);
                  setSchedulingWorkout(null);
                } catch (error) {
                  toast.error(
                    error instanceof Error ? error.message : 'Failed to add workout to calendar'
                  );
                } finally {
                  setIsScheduling(false);
                }
              }}
            >
              {isScheduling && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add to Calendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
